import { ArrowRight, ArrowUpRight, Database, Brain, Boxes, Cpu, BarChart3 } from "lucide-react";

const FUNCS = ["Sales", "Marketing", "Finance & Accounting", "Supply Chain", "Customer Service", "HR & People Ops"];
const SOLS = [
  { name: "AI Over BI", icon: BarChart3 },
  { name: "Enterprise Brain", icon: Brain },
  { name: "Data Products", icon: Boxes },
  { name: "AI Models", icon: Cpu },
  { name: "Dashboards & Reporting", icon: Database },
];

const Eyebrow = ({ children, gold }) => (
  <p className={`font-mono2 text-[10px] uppercase tracking-[0.28em] ${gold ? "text-[#E8AA68]" : "text-[#8A5730]"}`}>{children}</p>
);
const Title = ({ children, light }) => (
  <h4 className={`font-display text-xl lg:text-2xl font-medium tracking-tight mt-2 ${light ? "text-[#F7F5F0]" : "text-[#3E2818]"}`}>{children}</h4>
);
const Sub = ({ children, light }) => (
  <p className={`text-[12.5px] mt-2 max-w-2xl leading-relaxed ${light ? "text-white/65" : "text-[#3E2818]/60"}`}>{children}</p>
);

/* ---- Section 4 mini (tabs, light) — shown for context in every option ---- */
const MiniS4 = () => (
  <div className="rounded-xl bg-[#F7F5F0] border border-[#3E2818]/10 p-6">
    <Eyebrow>Business Function Solutions</Eyebrow>
    <Title>Transform Business Processes into Agentic Applications</Title>
    <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-[#3E2818]/12 mt-5 mb-4">
      {FUNCS.map((n, i) => (
        <span key={n} className={`relative pb-2.5 text-[12.5px] font-semibold ${i === 0 ? "text-[#3E2818]" : "text-[#3E2818]/45"}`}>
          {n}
          {i === 0 && <span className="absolute left-0 -bottom-px h-[2px] w-full bg-[#8A5730]" />}
        </span>
      ))}
    </div>
    <div className="rounded-lg border border-dashed border-[#3E2818]/15 h-20 flex items-center justify-center">
      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#3E2818]/35">Vertical · per-function detail</span>
    </div>
  </div>
);

/* ================= OPTION A · Parallel twins (light → dark) ================= */
const OptA = () => (
  <div className="space-y-4">
    <MiniS4 />
    <div className="rounded-xl bg-[#211307] p-6">
      <Eyebrow gold>Data &amp; Intelligence Solutions</Eyebrow>
      <Title light>Turn Enterprise Data into Decision Intelligence</Title>
      <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-white/12 mt-5 mb-4">
        {SOLS.map((s, i) => (
          <span key={s.name} className={`relative pb-2.5 text-[12.5px] font-semibold ${i === 0 ? "text-[#F7F5F0]" : "text-white/45"}`}>
            {s.name}
            {i === 0 && <span className="absolute left-0 -bottom-px h-[2px] w-full bg-[#E8AA68]" />}
          </span>
        ))}
      </div>
      <div className="rounded-lg border border-dashed border-white/15 h-20 flex items-center justify-center">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/35">Horizontal · per-solution detail</span>
      </div>
    </div>
  </div>
);

/* ================= OPTION B · Tabs (S4) vs horizontal card row (S5) ========= */
const OptB = () => (
  <div className="space-y-4">
    <MiniS4 />
    <div className="rounded-xl bg-[#F7F5F0] border border-[#3E2818]/10 p-6">
      <Eyebrow>Data &amp; Intelligence Solutions</Eyebrow>
      <Title>Turn Enterprise Data into Decision Intelligence</Title>
      <Sub>Build trusted context, intelligent data products, AI models and decision experiences that power agents across the enterprise.</Sub>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
        {SOLS.map((s, i) => (
          <div key={s.name} className="rounded-lg bg-white border border-[#3E2818]/10 p-3.5 hover:border-[#8A5730]/40 transition-colors">
            <div className="w-8 h-8 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-2.5"><s.icon size={15} strokeWidth={1.9} /></div>
            <p className="text-[12px] font-semibold text-[#3E2818] leading-snug">{s.name}</p>
            <span className="mt-2 inline-flex text-[#8A5730]"><ArrowUpRight size={14} /></span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ================= OPTION C · Platform stack (unified story) =============== */
const OptC = () => (
  <div className="rounded-xl bg-gradient-to-b from-[#F7F5F0] to-[#EFE9DF] border border-[#3E2818]/10 p-6">
    <div className="flex items-start justify-between gap-4">
      <div>
        <Eyebrow>The Intugle Platform</Eyebrow>
        <Title>Verticals, powered by Horizontals</Title>
      </div>
    </div>

    {/* Vertical function pillars on top */}
    <div className="mt-6 flex items-center gap-2 mb-2">
      <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#8A5730]">Vertical · Business Functions</span>
      <span className="flex-1 h-px bg-[#3E2818]/10" />
    </div>
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
      {FUNCS.map((n) => (
        <div key={n} className="rounded-lg bg-[#211307] text-[#F7F5F0] px-2.5 py-4 text-center">
          <span className="text-[11px] font-semibold leading-tight">{n}</span>
        </div>
      ))}
    </div>

    {/* Connector */}
    <div className="flex justify-center my-2 text-[#8A5730]/50">
      <ArrowRight size={16} className="rotate-90" />
    </div>

    {/* Horizontal data layers beneath */}
    <div className="flex items-center gap-2 mb-2">
      <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#8A5730]">Horizontal · Data &amp; Intelligence</span>
      <span className="flex-1 h-px bg-[#3E2818]/10" />
    </div>
    <div className="space-y-2">
      {SOLS.map((s) => (
        <div key={s.name} className="rounded-lg bg-[#FBF2E1] border border-[#E8AA68]/40 px-4 py-3 flex items-center gap-3">
          <div className="w-7 h-7 rounded-md bg-[#E8AA68]/30 text-[#8A5730] flex items-center justify-center shrink-0"><s.icon size={14} strokeWidth={1.9} /></div>
          <span className="text-[12.5px] font-semibold text-[#3E2818]">{s.name}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ================= OPTION D · Left-rail nav for S5 ========================= */
const OptD = () => (
  <div className="space-y-4">
    <MiniS4 />
    <div className="rounded-xl bg-[#211307] p-6">
      <Eyebrow gold>Data &amp; Intelligence Solutions</Eyebrow>
      <Title light>Turn Enterprise Data into Decision Intelligence</Title>
      <div className="grid md:grid-cols-[0.4fr_1fr] gap-5 mt-5">
        <div className="flex flex-col gap-1.5">
          {SOLS.map((s, i) => (
            <div key={s.name} className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 ${i === 0 ? "bg-white/10 text-[#F7F5F0]" : "text-white/55"}`}>
              <s.icon size={14} className={i === 0 ? "text-[#E8AA68]" : "text-white/40"} />
              <span className="text-[12px] font-semibold">{s.name}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-dashed border-white/15 flex items-center justify-center min-h-[160px]">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/35">Selected solution detail</span>
        </div>
      </div>
    </div>
  </div>
);

const OPTIONS = [
  { id: "a", title: "Option A · Parallel Twins (light → dark)", note: "Section 5 mirrors Section 4's exact tab pattern but flips to a dark espresso theme with gold accents. Reads as two sibling 'solution' families and creates a light→dark rhythm down the page.", comp: OptA },
  { id: "b", title: "Option B · Tabs (S4) vs Horizontal Card Row (S5)", note: "Keep functions tabbed; present the data solutions as a single horizontal row of 5 cards. The layout itself expresses 'horizontal' offerings and clearly differentiates the two sections.", comp: OptB },
  { id: "c", title: "Option C · Platform Stack (one unified story)", note: "A single combined visual: business functions (verticals) sit on top as pillars, powered by the data & intelligence layers (horizontals) beneath. Best if you want to tell the 'how it all fits' story in one hero moment.", comp: OptC },
  { id: "d", title: "Option D · Left-Rail Detail (S5)", note: "Section 5 uses a vertical left-rail selector with a detail panel — deliberately opposite to Section 4's top tabs, so the two sections feel distinct while both being explorable.", comp: OptD },
];

export default function SectionLayoutOptions() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Sections 4 &amp; 5 · Layout Ideas</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">How to show Vertical (functions) &amp; Horizontal (data) solutions</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Each option shows Section 4 (functions) for context and a treatment for Section 5 (data &amp; intelligence). These are compact concept mockups — tell me the letter and I&rsquo;ll build the real thing.</p>

        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
